import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import uuidv4 from 'uuidv4';

import Loading from 'xpi-black-ui/Base/Loading';
import { P } from 'xpi-black-ui/Base/Typography';
import ContentBox from 'xpi-black-ui/Base/ContentBox';
import { convertNumberBR } from 'xpi-black-ui/Utils/helpers';

import ModalDialog from '../ModalDialog';
import Header from './compose/Header';
import Configuration from './compose/Configuration';

import { getSpot, getPrice, postQuote } from '../../service';
import { LoadQuotes, SetQuoteEdit } from 'redux-flow/actions/custom-quotes';

const titlesCleanQuote = {
  clear: 'Você deseja excluir a cotação atual?',
  new: 'Você deseja excluir a cotação atual para iniciar uma nova?',
}

function CreateQuote({ strategies, assets, quoteEdit, msg }) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [legs, setLegs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [isQuoteUpdate, setIsQuoteUpdate] = useState(null);
  const [alreadyQuoted, setAlreadyQuoted] = useState(false);
  const [enableUpdatePrice, setEnableUpdatePrice] = useState(false);
  const [callBackCleanQuote, setCallBackCleanQuote] = useState(false);
  const [openModalCleanQuote, setOpenModalCleanQuote] = useState(false);
  const [titleModalCleanQuote, setTitleModalCleanQuote] = useState(titlesCleanQuote.clear);

  useEffect(() => {
    if (legs?.length > 0) {
      const allLegsIsValid = legs
        .filter(leg => !leg.compraAtivo)
        .every(leg => leg.strike.value && (leg?.quantity || leg?.quantity?.value) && leg.maturityDate);

      setEnableUpdatePrice(allLegsIsValid);
    }
  }, [legs]);

  useEffect(() => {
    if (quoteEdit) {
      addToConfiguration({
        edit: true,
        asset: quoteEdit.asset,
        reverse: quoteEdit.reverse,
        buyAsset: quoteEdit.compraAtivo,
        structure: quoteEdit.idEstruturaNome,
      })
    }
  }, [quoteEdit]);

  const loadPrice = async () => {
    const response = await getPrice({
      legs,
      callBack: () => setLoadingPrice(false),
      beforeBack: () => setLoadingPrice(true),
      commission: priceInfo?.commissionSelected || 0
    });

    const { data } = response;

    if (data) {
      return {
        ...data,
        commissionSelected: priceInfo?.commissionSelected || data?.commissionMinimum || 0
      };
    }

    return null;
  }

  const loadSpot = async (asset) => {
    const response = await getSpot({
      beforeBack: () => setLoading(true),
      callBack: () => setLoading(false),
      asset
    });

    const { spot } = response;

    if (spot) {
      return spot;
    }

    return null;
  };

  const saveQuote = async ({ quoteName }) => {
    const { commissionSelected, endPrice } = priceInfo;

    const response = await postQuote({
      beforeBack: () => setLoading(true),
      callBack: () => setLoading(false),
      data: { quoteName, commission: commissionSelected, endPrice, legs, isQuoteUpdate, quoteEdit }
    });

    if (response) {
      setLegs([]);
      dispatch(LoadQuotes());
    }
    if (isQuoteUpdate) {
      dispatch(SetQuoteEdit({}));
      history.push('/cotacoes-personalizadas/armazenadas');
    }
  };

  const inverseOperation = (value) => {
    if (value === "C") { return "V" }
    if (value === "V") { return "C" }

    return value;
  };

  const normalizeStrategieReverse = (strategie) => {
    let strategieUpdated = strategie?.map(leg => ({
      ...leg, operacao: inverseOperation(leg.operacao)
    }));

    let strategieReverse = [...strategieUpdated];

    strategieReverse = strategieReverse.map(leg => ({
      ...leg, reverse: true, operacao: inverseOperation(leg.operacao)
    }));
    return strategieUpdated.concat(strategieReverse);
  }

  const createLegs = ({ spot, asset, structure, maturityDate, maturityDateReverse, buyAsset, reverse, edit }) => {
    const strategie = strategies.find(strategie => structure === strategie.idEstruturaNome);

    const hasStockInStrategie = strategie?.compraAtivo;

    if (!edit) {
      strategie.vertices = strategie?.vertices
        ?.map(leg => ({ ...leg, isVisible: leg.compraAtivo ? buyAsset : true }))
        ?.filter(leg => leg.isVisible);

      if (reverse) {
        strategie.vertices = normalizeStrategieReverse(strategie?.vertices);
      }
    } else {
      strategie.vertices = quoteEdit?.vertices;
    }

    return strategie?.vertices.map(strategieLeg => ({
      ...strategieLeg,
      spot,
      asset,
      reverse,
      buyAsset,
      structure,
      hasStockInStrategie,
      nameStructure: strategie?.nomeEstrutura,
      idEstruturaNome: strategie?.idEstruturaNome,
      spotFormatted: convertNumberBR(spot, 2),
      quantity: strategieLeg?.quantity?.value || 1,
      maturityDate: strategieLeg.reverse
        ? strategieLeg?.maturityDate || maturityDateReverse
        : strategieLeg?.maturityDate || maturityDate,
    }))
  };

  const addIdInLegs = (legs) => (
    legs?.map(leg => ({
      ...leg,
      id: uuidv4(),
    })) || []
  );

  const addNewLegs = (newLegs, cleanPrevLegs = false) => {
    const legsWithId = addIdInLegs(newLegs);

    if (cleanPrevLegs) {
      setLegs(legsWithId);
    } else {
      setLegs(legs.concat(legsWithId));
    }
  };

  const removeLeg = (legToRemove) => {
    const legsUpdated = legs.filter(leg => leg.id !== legToRemove.id);
    setLegs(legsUpdated);
  };

  const updateLeg = (legUptaded) => {
    const legsUpdated = legs.map(leg => (
      leg.id === legUptaded.id
        ? {...legUptaded}
        : {...leg}
    ));
    setLegs(legsUpdated);
  };

  const onUpdatePrice = async () => {
    const price = await loadPrice();

    if (price) {
      setPriceInfo(price);
      setAlreadyQuoted(true);
    }
  };

  const clearCurrentQuote = () => {
    setLegs([]);
    setPriceInfo(null);
    setAlreadyQuoted(false);
  };

  const addToConfiguration = ({ asset, structure, maturityDate, maturityDateReverse, buyAsset, reverse, edit }) => {
    const startNewQuote = async () => {
      clearCurrentQuote();
      const spot = await loadSpot(asset);

      if (spot) {
        const newLegs = createLegs({ spot, reverse, buyAsset, maturityDate, maturityDateReverse, asset, structure, edit });
        setIsQuoteUpdate(edit);
        addNewLegs(newLegs, true);
      }
    }

    if (legs?.length === 0) {
      startNewQuote();
    } else {
      setTitleModalCleanQuote(titlesCleanQuote.new);
      setCallBackCleanQuote(() => startNewQuote);
      setOpenModalCleanQuote(true);
    }
  };

  const clickCleanQuote = () => {
    setTitleModalCleanQuote(titlesCleanQuote.clear);
    setCallBackCleanQuote(() => clearCurrentQuote);
    setOpenModalCleanQuote(true);
  };

  return (
    <>
      {msg ? (
      <ContentBox style={{ marginBottom: '24px' }}>
        <P style={{ textAlign: 'center' }}>{msg}</P>
      </ContentBox>
      ) : (
      <>
        <ContentBox>
          <Header
            assets={assets}
            strategies={strategies}
            addToConfiguration={addToConfiguration}
          />
        </ContentBox>
        {legs?.length > 0 && (
          <ContentBox style={{ marginTop: '32px' }}>
            <Configuration
              legs={legs}
              saveQuote={saveQuote}
              updateLeg={updateLeg}
              removeLeg={removeLeg}
              priceInfo={priceInfo}
              addNewLegs={addNewLegs}
              setPriceInfo={setPriceInfo}
              loadingPrice={loadingPrice}
              isQuoteUpdate={isQuoteUpdate}
              alreadyQuoted={alreadyQuoted}
              onUpdatePrice={onUpdatePrice}
              clickCleanQuote={clickCleanQuote}
              enableUpdatePrice={enableUpdatePrice}
            />
          </ContentBox>
        )}

        {loading && <Loading text="Carregando..." fixed />}

        <ModalDialog
          width="340px"
          height="auto"
          titlePrimaryButton="Sim"
          titleSecondaryButton="Não"
          open={openModalCleanQuote}
          title={titleModalCleanQuote}
          onClose={() => setOpenModalCleanQuote(false)}
          actionPrimaryButton={() => { callBackCleanQuote(); setOpenModalCleanQuote(false) }}
        />
      </>
      )}
    </>
  );
}

export default CreateQuote;
